import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { Button } from 'components/Button';
import { ICONS } from 'components/Icon';
import { NavigationStacks, Screens, Stacks } from 'components/Navigation/types';
import { Page } from 'components/Page';
import { useSelector } from 'hooks/useSelector';
import { theme } from 'lib/theme';
import React from 'react';
import { ListRenderItem } from '@shopify/flash-list';
import { View } from 'react-native';
import { useDispatch } from 'react-redux';
import {
  REMOTE_ASESETUPLIBRARY_QUESTIONOPEN,
  REMOTE_ASESETUP_COLLAPSEFOLDERSECTIONS,
  REMOTE_ASESETUP_FOLDERDELETE,
  REMOTE_ASESETUP_FOLDEROPEN,
  REMOTE_ASESETUP_SECTIONOPEN,
} from 'store/actions';
import {
  selectAseSetupLibraryFolder,
  selectAseSetupLibraryFolderItemList,
} from 'store/remote/selectors';
import { AssessmentQuestionType } from 'types/types';
import { SectionListItem } from './ListItems/SectionListItem';
import { List } from 'components/List';
import { PageEmpty } from 'components/PageEmpty';
import { PageHeader } from 'components/PageHeader';
import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';

type Props = NativeStackScreenProps<
  NavigationStacks[Stacks.ASSESSMENTSETUPLIBRARYEXPLORER],
  Screens.ASSESSMENTSETUPLIBRARYEXPLORERSECTIONS
>;

export const SectionListScreen: React.FC<Props> = ({ navigation, route }) => {
  const dispatch = useDispatch();
  const { id: folderId } = route.params;
  const folder = useSelector((state) =>
    selectAseSetupLibraryFolder(state, folderId),
  );
  const items = useSelector((state) =>
    selectAseSetupLibraryFolderItemList(state, folderId),
  );

  const handleCreateSection = () =>
    dispatch(REMOTE_ASESETUP_SECTIONOPEN({ folderId }));

  React.useEffect(() => {
    if (!folder?.id) {
      navigation.goBack();
    }
  }, [folder?.id, navigation]);

  // ListRenderItem<ReturnType<typeof selectAseSetupLibraryFolderItemList>[number]>

  const renderItem: ListRenderItem<
    ReturnType<typeof selectAseSetupLibraryFolderItemList>[number]
  > = React.useCallback(
    ({ item }) => {
      const { type } = item;
      if (type === 'addquestion') {
        return (
          <Card contentHorizontal style={{ marginBottom: theme.padding }}>
            <CardRow
              style={{ flex: 1, minHeight: 0 }}
              label="New Checklist"
              icon={ICONS.CircleCSolid}
              labelColor={theme.colors.blue}
              iconColor={theme.colors.checklist}
              onPress={() =>
                dispatch(
                  REMOTE_ASESETUPLIBRARY_QUESTIONOPEN({
                    sectionId: item.id,
                    type: AssessmentQuestionType.checklist,
                  }),
                )
              }
            />
            <CardRow
              style={{ flex: 1, minHeight: 0 }}
              label="New Question"
              icon={ICONS.CircleQSolid}
              labelColor={theme.colors.blue}
              iconColor={theme.colors.question}
              onPress={() =>
                dispatch(
                  REMOTE_ASESETUPLIBRARY_QUESTIONOPEN({
                    sectionId: item.id,
                    type: AssessmentQuestionType.question,
                  }),
                )
              }
            />
          </Card>
        );
      }
      if (type === 'addsection') {
        return (
          <View style={{ alignItems: 'center', padding: theme.padding }}>
            <Button
              text="New Section"
              onPress={() =>
                dispatch(REMOTE_ASESETUP_SECTIONOPEN({ folderId: item.id }))
              }
            />
          </View>
        );
      }
      return (
        <SectionListItem
          type={type}
          id={item.id}
          level={item.level}
          last={item.last}
        />
      );
    },
    [dispatch],
  );

  if (!folder) {
    return null;
  }

  return (
    <Page
      header={
        <PageHeader
          title="Checklists & Questions"
          subtitle={folder.name}
          onPressBackButton={navigation.goBack}
          menuRight={{
            iconButtonConfig: {
              icon: ICONS.EllipsisLight,
            },
            options: [
              {
                text: 'Rename Folder',
                icon: ICONS.PencilLight,
                onPress: () =>
                  dispatch(REMOTE_ASESETUP_FOLDEROPEN({ id: folderId })),
              },
              {
                text: 'Delete Folder',
                icon: ICONS.TrashCanLight,
                destructive: true,
                onPress: () =>
                  dispatch(REMOTE_ASESETUP_FOLDERDELETE({ id: folderId })),
              },
              '-',
              {
                text: 'New Section',
                onPress: handleCreateSection,
                icon: ICONS.PlusLight,
              },
              {
                text: 'Expand Sections',
                onPress: () =>
                  dispatch(
                    REMOTE_ASESETUP_COLLAPSEFOLDERSECTIONS({
                      folderId,
                      collapse: false,
                    }),
                  ),
              },
              {
                text: 'Collapse Sections',
                onPress: () =>
                  dispatch(
                    REMOTE_ASESETUP_COLLAPSEFOLDERSECTIONS({
                      folderId,
                      collapse: true,
                    }),
                  ),
              },
            ],
          }}
        />
      }
    >
      {items.length ? (
        <List
          data={items}
          keyExtractor={(item) => `${item.type}-${item.id}`}
          renderItem={renderItem}
        />
      ) : (
        <PageEmpty
          icon={ICONS.RBPlectrumLight}
          button={{ text: 'New Section', onPress: handleCreateSection }}
        />
      )}
    </Page>
  );
};
